import React, { useState, useEffect, useContext } from 'react';
import { api } from './api/api';
import { AuthContext } from '../App';

const TEAM_ROLES = [
    { value: '',            label: 'All teams'   },
    { value: 'FORCE_ONE',   label: 'Force One'   },
    { value: 'BRUTE_FORCE', label: 'Brute Force' },
    { value: 'MIDLAND',     label: 'Midland'     },
    { value: 'SUPERNOVA',   label: 'Supernova'   },
    { value: 'RUSSIA',      label: 'Russia'      },
    { value: 'FRIEND',      label: 'Friend'      },
    { value: 'GUEST',       label: 'Guest'       },
    { value: 'OTHER',       label: 'Other'       },
];

const buildParams = (teamRole, userIds) => {
    const p = new URLSearchParams();
    if (userIds.length > 0) p.set('userIds', userIds.join(','));
    else if (teamRole) p.set('teamRole', teamRole);
    return p.toString() ? `?${p}` : '';
};

// ── Constants ──────────────────────────────────────────────────────────────

const PARTS = [
    { key: 'chassis',     label: 'Chassis' },
    { key: 'engine',      label: 'Engine' },
    { key: 'frontWing',   label: 'Front Wing' },
    { key: 'rearWing',    label: 'Rear Wing' },
    { key: 'underbody',   label: 'Underbody' },
    { key: 'sidepods',    label: 'Sidepods' },
    { key: 'cooling',     label: 'Cooling' },
    { key: 'gear',        label: 'Gearbox' },
    { key: 'brakes',      label: 'Brakes' },
    { key: 'suspension',  label: 'Suspension' },
    { key: 'electronics', label: 'Electronics' },
];

const TABS = [
    { id: 'overview',   label: 'Overview' },
    { id: 'qualifying', label: 'Qualifying' },
    { id: 'driver',     label: 'Driver' },
    { id: 'car',        label: 'Car & Parts' },
    { id: 'risks',      label: 'Risks & Overtakes' },
    { id: 'energy',     label: 'Energy & Fuel' },
];

// ── Helpers ────────────────────────────────────────────────────────────────

const wearColor = (pct) => {
    if (pct < 30) return '#4caf50';
    if (pct < 60) return '#ff9800';
    return '#f44336';
};

const WearBar = ({ value }) => {
    const v = Math.min(Math.max(value ?? 0, 0), 100);
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{ width: 64, height: 8, background: '#e0e0e0', borderRadius: 4, overflow: 'hidden' }}>
                <div style={{ width: `${v}%`, height: '100%', background: wearColor(v), borderRadius: 4 }} />
            </div>
            <span style={{ fontSize: 12, color: wearColor(v), fontWeight: 600, minWidth: 30 }}>{v}%</span>
        </div>
    );
};

const StatBar = ({ value, max = 100 }) => {
    const v = value ?? 0;
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{ width: 64, height: 8, background: '#e0e0e0', borderRadius: 4, overflow: 'hidden' }}>
                <div style={{ width: `${Math.min((v / max) * 100, 100)}%`, height: '100%', background: '#1976d2', borderRadius: 4 }} />
            </div>
            <span style={{ fontSize: 12, minWidth: 24 }}>{v}</span>
        </div>
    );
};

const PosDelta = ({ start, finish }) => {
    const d = (start ?? 0) - (finish ?? 0);
    const color = d > 0 ? '#4caf50' : d < 0 ? '#f44336' : '#999';
    return (
        <span style={{ color, fontWeight: 700, fontSize: 13 }}>
            {d > 0 ? `+${d}` : d}
        </span>
    );
};

// Transposed comparison table: rows = fields, cols = managers
const CompareTable = ({ rows, managers }) => (
    <div className="fb-table-wrap">
        <table className="fb-table ra-table">
            <thead>
                <tr>
                    <th style={{ minWidth: 160 }}>Field</th>
                    {managers.map((m, i) => (
                        <th key={i}>{m.driverName ?? `Manager ${i + 1}`}</th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {rows.map((row, i) => (
                    <tr key={i}>
                        <td style={{ color: 'var(--c-text-muted)', fontWeight: 500, fontSize: 12, background: '#fafafa' }}>
                            {row.label}
                        </td>
                        {managers.map((m, j) => (
                            <td key={j}>
                                {row.render ? row.render(m) : (m[row.key] ?? '—')}
                            </td>
                        ))}
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
);

const SectionTitle = ({ children }) => (
    <div style={{ padding: '16px 0 8px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, color: 'var(--c-text-muted)' }}>
        {children}
    </div>
);

// ── Tab renderers ──────────────────────────────────────────────────────────

const TabOverview = ({ managers }) => {
    const rows = [
        { label: 'Start Position',  key: 'startPosition' },
        { label: 'Finish Position', key: 'finishPosition' },
        { label: 'Pos. Change',     render: m => <PosDelta start={m.startPosition} finish={m.finishPosition} /> },
        { label: 'Total Time',      key: 'totalTime' },
        { label: 'Best Lap',        key: 'bestLap' },
        { label: 'Avg Lap',         key: 'avgLap' },
        { label: 'Pit Stops',       key: 'pitStops' },
        { label: 'Weather',         key: 'weather' },
        { label: 'Tyre Supplier',   key: 'tyreSupplier' },
        { label: 'Tyre Compound',   key: 'tyreCompound' },
    ];
    return <CompareTable rows={rows} managers={managers} />;
};

const TabQualifying = ({ managers }) => {
    const rows = [
        { label: 'Q1 Time',       key: 'q1Time' },
        { label: 'Q1 Net Time',   key: 'q1NetTime' },
        { label: 'Q1 Position',   key: 'q1Pos' },
        { label: 'Q1 Risk',       key: 'q1Risk' },
        { label: 'Q2 Time',       key: 'q2Time' },
        { label: 'Q2 Net Time',   key: 'q2NetTime' },
        { label: 'Q2 Position',   key: 'q2Pos' },
        { label: 'Q2 Risk',       key: 'q2Risk' },
        { label: 'Start Position', key: 'startPosition' },
    ];
    return <CompareTable rows={rows} managers={managers} />;
};

const TabDriver = ({ managers }) => {
    const rows = [
        { label: 'Overall',        render: m => <StatBar value={m.driverOverall}        max={300} /> },
        { label: 'Concentration',  render: m => <StatBar value={m.driverConcentration}  max={250} /> },
        { label: 'Talent',         render: m => <StatBar value={m.driverTalent}         max={250} /> },
        { label: 'Aggressiveness', render: m => <StatBar value={m.driverAggressiveness} max={250} /> },
        { label: 'Experience',     render: m => <StatBar value={m.driverExperience}     max={250} /> },
        { label: 'Tech Insight',   render: m => <StatBar value={m.driverTechInsight}    max={250} /> },
        { label: 'Stamina',        render: m => <StatBar value={m.driverStamina}        max={250} /> },
        { label: 'Charisma',       render: m => <StatBar value={m.driverCharisma}       max={250} /> },
        { label: 'Motivation',     render: m => <StatBar value={m.driverMotivation}     max={250} /> },
        { label: 'Reputation',     render: m => <StatBar value={m.driverReputation}     max={250} /> },
        { label: 'Weight',         render: m => `${m.driverWeight ?? '—'} kg` },
    ];
    return <CompareTable rows={rows} managers={managers} />;
};

const TabCar = ({ managers }) => {
    const perfRows = [
        { label: 'Power — Base',          render: m => m.carPowerBase ?? '—' },
        { label: 'Power — Testing (CCP)', render: m => m.carPowerTest != null ? <span style={{ color: '#1976d2', fontWeight: 600 }}>+{m.carPowerTest}</span> : <span style={{ color: '#aaa' }}>—</span> },
        { label: 'Power — Total',         render: m => <strong>{m.carPower ?? '—'}</strong> },
        { label: 'Handling — Base',           render: m => m.carHandlingBase ?? '—' },
        { label: 'Handling — Testing (CCP)',  render: m => m.carHandlingTest != null ? <span style={{ color: '#1976d2', fontWeight: 600 }}>+{m.carHandlingTest}</span> : <span style={{ color: '#aaa' }}>—</span> },
        { label: 'Handling — Total',          render: m => <strong>{m.carHandling ?? '—'}</strong> },
        { label: 'Acceleration — Base',          render: m => m.carAccelBase ?? '—' },
        { label: 'Acceleration — Testing (CCP)', render: m => m.carAccelTest != null ? <span style={{ color: '#1976d2', fontWeight: 600 }}>+{m.carAccelTest}</span> : <span style={{ color: '#aaa' }}>—</span> },
        { label: 'Acceleration — Total',         render: m => <strong>{m.carAcceleration ?? '—'}</strong> },
    ];

    const partRows = PARTS.flatMap(({ key, label }) => [
        {
            label: `${label} — Level`,
            render: m => m[`${key}Lvl`] ?? '—',
        },
        {
            label: `${label} — Wear`,
            render: m => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <WearBar value={m[`${key}StartWear`]} />
                    <span style={{ color: '#aaa', fontSize: 11 }}>→</span>
                    <WearBar value={m[`${key}FinishWear`]} />
                </div>
            ),
        },
    ]);

    return (
        <div>
            <SectionTitle>Car Performance</SectionTitle>
            <CompareTable rows={perfRows} managers={managers} />
            <SectionTitle>Parts Wear (start → finish)</SectionTitle>
            <CompareTable rows={partRows} managers={managers} />
        </div>
    );
};

const TabRisks = ({ managers }) => {
    const overtakeRows = [
        { label: 'Overtake Attempts',    key: 'oaAttempts' },
        { label: 'Successful Overtakes', key: 'overtakes' },
        { label: 'Attacks on You',       key: 'otAttemptsOnYou' },
        { label: 'Times Overtaken',      key: 'overtakesOnYou' },
    ];
    const riskRows = [
        { label: 'Start Risk',       key: 'startRisk' },
        { label: 'Overtake Risk %',  key: 'overtakeRisk' },
        { label: 'Defend Risk %',    key: 'defendRisk' },
        { label: 'Clear Dry Risk %', key: 'clearDryRisk' },
        { label: 'Clear Wet Risk %', key: 'clearWetRisk' },
        { label: 'Problem Risk %',   key: 'problemRisk' },
    ];
    return (
        <div>
            <SectionTitle>Overtakes</SectionTitle>
            <CompareTable rows={overtakeRows} managers={managers} />
            <SectionTitle>Risk Settings</SectionTitle>
            <CompareTable rows={riskRows} managers={managers} />
        </div>
    );
};

const TabEnergy = ({ managers }) => {
    const energyRows = [
        { label: 'Energy Q1',   render: m => m.energyQ1From != null ? `${m.energyQ1From} → ${m.energyQ1To}` : '—' },
        { label: 'Energy Q2',   render: m => m.energyQ2From != null ? `${m.energyQ2From} → ${m.energyQ2To}` : '—' },
        { label: 'Energy Race', render: m => m.energyRaceFrom != null ? `${m.energyRaceFrom} → ${m.energyRaceTo}` : '—' },
    ];
    const fuelRows = [
        { label: 'Start Fuel (L)',   key: 'startFuel' },
        { label: 'Finish Fuel (L)',  key: 'finishFuel' },
        { label: 'Fuel Used (L)',    key: 'fuelUsed' },
        { label: 'Finish Tyres',     render: m => m.finishTyres != null ? <WearBar value={m.finishTyres} /> : '—' },
        { label: 'Tyres Used',       render: m => m.tyresUsed != null ? `${m.tyresUsed}%` : '—' },
    ];
    return (
        <div>
            <SectionTitle>Driver Energy</SectionTitle>
            <CompareTable rows={energyRows} managers={managers} />
            <SectionTitle>Fuel & Tyres</SectionTitle>
            <CompareTable rows={fuelRows} managers={managers} />
        </div>
    );
};

// ── Rain badge ─────────────────────────────────────────────────────────────

const RainBadge = () => (
    <span style={{
        display: 'inline-block',
        fontSize: 9, fontWeight: 700,
        background: '#1d4ed8', color: '#fff',
        borderRadius: 3, padding: '1px 4px',
        marginRight: 6, verticalAlign: 'middle',
        letterSpacing: 0.3,
    }}>
        RAIN
    </span>
);

// ── Main component ─────────────────────────────────────────────────────────

const RaceAnalysis = () => {
    const { userRole } = useContext(AuthContext);
    const isAdmin = userRole === 'ADMIN';

    const [raceList, setRaceList]               = useState([]);
    const [selectedRace, setSelectedRace]       = useState(null);
    const [raceData, setRaceData]               = useState([]);
    const [activeTab, setActiveTab]             = useState('overview');
    const [loading, setLoading]                 = useState(true);
    const [detailLoading, setDetailLoading]     = useState(false);
    const [error, setError]                     = useState(null);
    const [teamRole, setTeamRole]               = useState('');
    const [allUsers, setAllUsers]               = useState([]);
    const [selectedUserIds, setSelectedUserIds] = useState([]);

    const userIdsKey = selectedUserIds.join(',');

    // Load users list for admin (for userIds filter)
    useEffect(() => {
        if (!isAdmin) return;
        api.get('/management/users').then(r => setAllUsers(r.data)).catch(() => {});
    }, [isAdmin]);

    const filteredUsers = teamRole
        ? allUsers.filter(u => u.role === teamRole)
        : allUsers;

    const toggleUserId = (id) => {
        setSelectedUserIds(prev =>
            prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
        );
    };

    // ── Load race list ──────────────────────────────────────────────
    useEffect(() => {
        const load = async () => {
            setLoading(true);
            setError(null);
            setSelectedRace(null);
            setRaceData([]);
            try {
                const res = await api.get(`/race-analysis${buildParams(teamRole, selectedUserIds)}`);
                setRaceList(
                    res.data.map(r => ({ ...r, key: `${r.season}_${r.raceNumber}` }))
                );
            } catch (err) {
                console.error(err);
                setError('Failed to load races.');
            } finally {
                setLoading(false);
            }
        };
        load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [teamRole, userIdsKey]);

    // Clear userIds when team changes
    useEffect(() => {
        setSelectedUserIds([]);
    }, [teamRole]);

    // ── Load full analysis on race click ──────────────────────────
    const selectRace = async (race) => {
        setSelectedRace(race);
        setActiveTab('overview');
        setDetailLoading(true);
        try {
            const res = await api.get(`/race-analysis/${race.season}/${race.raceNumber}${buildParams(teamRole, selectedUserIds)}`);
            setRaceData(res.data);
        } catch (err) {
            console.error(err);
            setRaceData([]);
        } finally {
            setDetailLoading(false);
        }
    };

    // Group by season for desktop sidebar
    const seasons = [...new Set(raceList.map(r => r.season))].sort((a, b) => b - a);
    const bySeason = seasons.reduce((acc, s) => {
        acc[s] = raceList.filter(r => r.season === s);
        return acc;
    }, {});

    // Mobile select handler
    const handleMobileSelect = (e) => {
        const race = raceList.find(r => r.key === e.target.value);
        if (race) selectRace(race);
    };

    const tabContent = {
        overview:   () => <TabOverview   managers={raceData} />,
        qualifying: () => <TabQualifying managers={raceData} />,
        driver:     () => <TabDriver     managers={raceData} />,
        car:        () => <TabCar        managers={raceData} />,
        risks:      () => <TabRisks      managers={raceData} />,
        energy:     () => <TabEnergy     managers={raceData} />,
    };

    // ── Styles ──

    const S = {
        sidebar: {
            width: 260,
            borderRight: '1px solid var(--c-border)',
            overflowY: 'auto',
            flexShrink: 0,
            background: '#fff',
        },
        sidebarHeader: {
            padding: '12px 16px',
            fontWeight: 700,
            fontSize: 11,
            borderBottom: '1px solid var(--c-border)',
            background: '#fafafa',
            textTransform: 'uppercase',
            letterSpacing: '0.6px',
            color: 'var(--c-text-muted)',
        },
        seasonLabel: {
            padding: '12px 16px 4px',
            fontSize: 10,
            fontWeight: 700,
            color: 'var(--c-text-light)',
            textTransform: 'uppercase',
            letterSpacing: 1.2,
        },
        raceItem: (selected) => ({
            padding: '9px 16px',
            cursor: 'pointer',
            background: selected ? '#fff3f3' : 'transparent',
            borderLeft: `3px solid ${selected ? 'var(--c-accent)' : 'transparent'}`,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            transition: 'background 0.12s',
        }),
        raceName: { fontSize: 13, fontWeight: 600, color: 'var(--c-text)' },
        raceDate: { fontSize: 11, color: 'var(--c-text-light)' },
        main: {
            flex: 1,
            overflowY: 'auto',
            padding: 24,
            background: 'var(--c-bg)',
            minWidth: 0,
        },
        placeholder: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            color: 'var(--c-text-light)',
            gap: 12,
        },
        raceHeader: {
            marginBottom: 20,
            paddingBottom: 16,
            borderBottom: '1px solid var(--c-border)',
        },
        raceTitle: { margin: '4px 0 6px', fontSize: 20, fontWeight: 700 },
        raceMeta: { fontSize: 13, color: 'var(--c-text-muted)', display: 'flex', gap: 14 },
        tabBar: {
            display: 'flex',
            borderBottom: '2px solid var(--c-border)',
            marginBottom: 20,
            flexWrap: 'wrap',
        },
        tabBtn: (active) => ({
            padding: '9px 18px',
            cursor: 'pointer',
            border: 'none',
            background: 'none',
            fontWeight: active ? 700 : 400,
            fontSize: 13,
            color: active ? 'var(--c-accent)' : 'var(--c-text-muted)',
            borderBottom: `2px solid ${active ? 'var(--c-accent)' : 'transparent'}`,
            marginBottom: -2,
            transition: 'color 0.15s',
        }),
    };

    return (
        <div className="ra-page">
            {/* ── Desktop race list sidebar ── */}
            <div style={S.sidebar} className="ra-desktop-sidebar">
                <div style={S.sidebarHeader}>Races</div>
                {isAdmin && (
                    <div style={{ padding: '10px 12px', borderBottom: '1px solid var(--c-border)', background: '#fafafa' }}>
                        <select value={teamRole} onChange={e => setTeamRole(e.target.value)}
                            style={{ width: '100%', padding: '5px 8px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--c-border)', fontSize: 12, background: '#fff', color: 'var(--c-text)', cursor: 'pointer', marginBottom: filteredUsers.length > 0 ? 8 : 0 }}>
                            {TEAM_ROLES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                        {filteredUsers.length > 0 && (
                            <div style={{ maxHeight: 130, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {filteredUsers.map(u => (
                                    <label key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '2px 0', fontSize: 12, cursor: 'pointer', color: 'var(--c-text-2)' }}>
                                        <input type="checkbox" checked={selectedUserIds.includes(u.id)} onChange={() => toggleUserId(u.id)} />
                                        {u.firstname ? `${u.firstname} ${u.lastname || ''}`.trim() : u.email}
                                    </label>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {loading && (
                    <div style={{ padding: 16, color: 'var(--c-text-muted)', fontSize: 13 }}>
                        Loading…
                    </div>
                )}
                {error && (
                    <div style={{ padding: 16, color: 'var(--c-error)', fontSize: 13 }}>{error}</div>
                )}
                {!loading && !error && raceList.length === 0 && (
                    <div style={{ padding: 16, color: 'var(--c-text-muted)', fontSize: 13 }}>
                        No races found.
                    </div>
                )}

                {seasons.map(season => (
                    <div key={season}>
                        <div style={S.seasonLabel}>Season {season}</div>
                        {bySeason[season].map(race => {
                            const isSelected = selectedRace?.key === race.key;
                            return (
                                <div
                                    key={race.key}
                                    style={S.raceItem(isSelected)}
                                    onClick={() => selectRace(race)}
                                >
                                    <span style={S.raceName}>
                                        {race.hasRain && <RainBadge />}
                                        R{race.raceNumber}
                                        {race.trackName ? ` — ${race.trackName}` : ''}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                ))}
            </div>

            {/* ── Main content panel ── */}
            <div style={S.main} className="ra-main">
                {/* Mobile race selector (hidden on desktop via CSS) */}
                <div className="ra-mobile-selector">
                    <select
                        className="fb-select"
                        style={{ width: '100%' }}
                        value={selectedRace?.key || ''}
                        onChange={handleMobileSelect}
                        disabled={loading}
                    >
                        <option value="">{loading ? 'Loading races…' : '— Select a race —'}</option>
                        {seasons.map(season => (
                            <optgroup key={season} label={`Season ${season}`}>
                                {bySeason[season].map(race => (
                                    <option key={race.key} value={race.key}>
                                        {race.label || `S${race.season} R${race.raceNumber}`}
                                    </option>
                                ))}
                            </optgroup>
                        ))}
                    </select>
                </div>

                {/* Placeholder when nothing selected */}
                {!selectedRace && (
                    <div style={S.placeholder} className="ra-desktop-placeholder">
                        <span style={{ fontSize: 44 }}>🏁</span>
                        <span style={{ fontSize: 14 }}>Select a race from the list</span>
                    </div>
                )}

                {/* Loading detail */}
                {selectedRace && detailLoading && (
                    <div style={{ color: 'var(--c-text-muted)', marginTop: 48, textAlign: 'center', fontSize: 14 }}>
                        Loading race data…
                    </div>
                )}

                {/* Race detail */}
                {selectedRace && !detailLoading && (
                    <>
                        {/* Header */}
                        <div style={S.raceHeader}>
                            <div style={{ fontSize: 11, color: 'var(--c-text-light)', textTransform: 'uppercase', letterSpacing: 1 }}>
                                Season {selectedRace.season}
                            </div>
                            <h2 style={S.raceTitle}>
                                {selectedRace.hasRain && <RainBadge />}
                                Race {selectedRace.raceNumber}
                                {selectedRace.trackName ? ` — ${selectedRace.trackName}` : ''}
                            </h2>
                            <div style={S.raceMeta}>
                                {raceData[0]?.weather && <span>{raceData[0].weather}</span>}
                                {raceData.length > 0 && (
                                    <span>{raceData.length} manager{raceData.length !== 1 ? 's' : ''}</span>
                                )}
                            </div>
                        </div>

                        {raceData.length === 0 ? (
                            <div style={{ color: 'var(--c-text-muted)', fontSize: 14 }}>
                                No data available for this race.
                            </div>
                        ) : (
                            <>
                                {/* Tabs */}
                                <div style={S.tabBar}>
                                    {TABS.map(tab => (
                                        <button
                                            key={tab.id}
                                            style={S.tabBtn(activeTab === tab.id)}
                                            onClick={() => setActiveTab(tab.id)}
                                        >
                                            {tab.label}
                                        </button>
                                    ))}
                                </div>

                                {/* Tab content */}
                                {tabContent[activeTab]?.()}
                            </>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default RaceAnalysis;
